import { Box, Paper, Stack, Typography } from '@mui/material';
import type { CompetitionPublic, OverallRanking, Result } from '../../types/results';
import type { Team } from '../../types/domain';
import { useAutoRotate } from '../../hooks/useAutoRotate';
import { MEDAL_COLORS, buildBoardRows, type BoardRow } from './BoardShared';
import { buildBoardSlots } from './boardSlots';

interface SplitBoardProps {
  competition: CompetitionPublic;
  results: Result[];
  overall: OverallRanking[];
  allTeams: Team[] | undefined;
}

/**
 * Horni polovina: matrix (kompaktni nahled vseho), dolni polovina: focus (aktualne rotujici).
 */
export const SplitBoard = ({ competition, results, overall, allTeams }: SplitBoardProps) => {
  const showAll = !!competition.boardShowAllTeams;
  const sortMode = competition.boardSortMode;
  const slots = buildBoardSlots(competition, results, overall);
  const { index } = useAutoRotate({
    total: slots.length,
    intervalMs: Math.max(5, competition.publicRotationSeconds ?? 10) * 1000,
  });
  const active = slots[index];

  return (
    <Box sx={{ position: 'relative', flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', gap: 2 }}>
      {/* Horní polovina: kompaktní matrix */}
      <Box
        sx={{
          flex: 1,
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            md: 'repeat(3, 1fr)',
            xl: 'repeat(4, 1fr)',
          },
          gap: 1,
          overflow: 'auto',
        }}
      >
        {slots.map((s, i) => (
          <Paper
            key={s.key}
            variant="outlined"
            sx={{
              p: 1,
              borderColor: i === index ? 'primary.main' : 'divider',
              borderWidth: i === index ? 2 : 1,
              bgcolor: i === index ? 'action.selected' : 'background.paper',
            }}
          >
            <Typography
              variant="caption"
              sx={{ fontWeight: 700, color: 'primary.main', letterSpacing: '0.08em', textTransform: 'uppercase' }}
            >
              {s.category.name}
            </Typography>
            <Typography fontWeight={700} sx={{ lineHeight: 1.1, fontSize: '0.95rem' }}>
              {s.kind === 'DISCIPLINE' ? s.discipline.name : 'Celkové pořadí'}
            </Typography>
            {s.kind === 'DISCIPLINE' ? (
              <MiniRows
                results={s.results}
                categoryId={s.category.id}
                disciplineId={s.discipline.id}
                allTeams={allTeams}
                showAll={showAll}
                sortMode={sortMode}
              />
            ) : (
              <MiniOverall ranking={s.ranking} />
            )}
          </Paper>
        ))}
      </Box>

      {/* Dolní polovina: focus aktivního slotu */}
      {active && (
        <Paper
          elevation={3}
          sx={{
            flex: 1,
            p: 2,
            display: 'flex',
            flexDirection: 'column',
            gap: 1,
            background:
              active.kind === 'OVERALL'
                ? 'linear-gradient(135deg, #C62828 0%, #B71C1C 100%)'
                : 'linear-gradient(135deg, #FFB300 0%, #FF8F00 100%)',
            color: '#FFFFFF',
            minHeight: 0,
          }}
        >
          <Typography variant="overline" sx={{ fontWeight: 700, letterSpacing: '0.15em', opacity: 0.85, color: 'inherit' }}>
            {active.category.name}
          </Typography>
          <Typography variant="h4" sx={{ fontWeight: 900, lineHeight: 1.1, color: 'inherit' }}>
            {active.kind === 'DISCIPLINE' ? active.discipline.name : 'Celkové pořadí'}
          </Typography>
          <Box sx={{ flex: 1, overflow: 'hidden' }}>
            {active.kind === 'DISCIPLINE' ? (
              <FocusRows
                results={active.results}
                categoryId={active.category.id}
                disciplineId={active.discipline.id}
                allTeams={allTeams}
                showAll={showAll}
                sortMode={sortMode}
              />
            ) : (
              <FocusOverall ranking={active.ranking} />
            )}
          </Box>
        </Paper>
      )}
    </Box>
  );
};

const MiniRows = ({
  results,
  categoryId,
  disciplineId,
  allTeams,
  showAll,
  sortMode,
}: {
  results: Result[];
  categoryId: number;
  disciplineId: number;
  allTeams: Team[] | undefined;
  showAll: boolean;
  sortMode: CompetitionPublic['boardSortMode'];
}) => {
  const rows: BoardRow[] = buildBoardRows(results, allTeams, categoryId, disciplineId, {
    showAll,
    sortMode,
    topN: 3,
  });
  if (rows.length === 0) return <Typography variant="caption" color="text.disabled">—</Typography>;
  return (
    <Stack spacing={0.25} sx={{ mt: 0.5 }}>
      {rows.map((r) => {
        const medal = r.rank != null ? MEDAL_COLORS[r.rank] : null;
        const leftLabel = sortMode === 'START_NUMBER' ? `#${r.teamStartNumber}` : (r.rank ?? '–');
        return (
          <Stack
            key={r.id}
            direction="row"
            spacing={1}
            alignItems="center"
            sx={{
              px: 0.5,
              borderLeft: medal ? `3px solid ${medal.bg}` : '3px solid transparent',
              opacity: r.__missing ? 0.55 : 1,
            }}
          >
            <Typography sx={{ fontWeight: 700, fontSize: '0.8rem', minWidth: 24 }}>{leftLabel}</Typography>
            <Typography
              sx={{ flex: 1, fontSize: '0.8rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
            >
              {r.teamName}
            </Typography>
            <Typography sx={{ fontFamily: 'monospace', fontSize: '0.8rem', fontWeight: 600 }}>
              {r.isNp ? 'NP' : r.timeFinalFormatted}
            </Typography>
          </Stack>
        );
      })}
    </Stack>
  );
};

const MiniOverall = ({ ranking }: { ranking: OverallRanking }) => {
  const top = [...ranking.teams].sort((a, b) => a.finalRank - b.finalRank).slice(0, 3);
  return (
    <Stack spacing={0.25} sx={{ mt: 0.5 }}>
      {top.map((t) => {
        const medal = MEDAL_COLORS[t.finalRank];
        return (
          <Stack
            key={t.teamId}
            direction="row"
            spacing={1}
            alignItems="center"
            sx={{ px: 0.5, borderLeft: medal ? `3px solid ${medal.bg}` : '3px solid transparent' }}
          >
            <Typography sx={{ fontWeight: 700, fontSize: '0.8rem', minWidth: 16 }}>{t.finalRank}</Typography>
            <Typography
              sx={{ flex: 1, fontSize: '0.8rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
            >
              {t.teamName}
            </Typography>
            <Typography sx={{ fontFamily: 'monospace', fontSize: '0.8rem', fontWeight: 600 }}>{t.totalPoints} b.</Typography>
          </Stack>
        );
      })}
    </Stack>
  );
};

const FocusRows = ({
  results,
  categoryId,
  disciplineId,
  allTeams,
  showAll,
  sortMode,
}: {
  results: Result[];
  categoryId: number;
  disciplineId: number;
  allTeams: Team[] | undefined;
  showAll: boolean;
  sortMode: CompetitionPublic['boardSortMode'];
}) => {
  const rows: BoardRow[] = buildBoardRows(results, allTeams, categoryId, disciplineId, {
    showAll,
    sortMode,
    topN: 6,
  });
  if (rows.length === 0) return <Typography sx={{ opacity: 0.75 }}>zatím žádné výsledky</Typography>;
  return (
    <Stack spacing={0.5}>
      {rows.map((r) => {
        const medal = r.rank != null ? MEDAL_COLORS[r.rank] : null;
        return (
          <Box
            key={r.id}
            sx={{
              display: 'grid',
              gridTemplateColumns: 'auto 60px 1fr auto',
              alignItems: 'center',
              gap: 2,
              px: 1,
              py: 0.5,
              bgcolor: medal ? 'rgba(255,255,255,0.18)' : 'transparent',
              borderRadius: 1,
              opacity: r.__missing ? 0.55 : 1,
            }}
          >
            <SplitRankBadge rank={r.rank} medal={medal} />
            <Typography sx={{ fontFamily: 'monospace', opacity: 0.8 }}>#{r.teamStartNumber}</Typography>
            <Typography sx={{ fontWeight: 700, fontSize: '1.25rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {r.teamName}
            </Typography>
            <Typography sx={{ fontFamily: 'monospace', fontWeight: 800, fontSize: '1.5rem' }}>
              {r.isNp ? 'NP' : r.timeFinalFormatted}
            </Typography>
          </Box>
        );
      })}
    </Stack>
  );
};

const SplitRankBadge = ({ rank, medal }: { rank: number | null; medal: { bg: string; fg: string } | null }) => {
  const bg = medal ? medal.bg : 'rgba(255,255,255,0.2)';
  const fg = medal ? medal.fg : '#FFFFFF';
  return (
    <Box
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: 44,
        height: 44,
        borderRadius: '999px',
        bgcolor: bg,
        color: fg,
        fontWeight: 900,
        fontSize: '1.4rem',
        fontVariantNumeric: 'tabular-nums',
        flexShrink: 0,
      }}
    >
      {rank ?? '–'}
    </Box>
  );
};

const FocusOverall = ({ ranking }: { ranking: OverallRanking }) => {
  const top = [...ranking.teams].sort((a, b) => a.finalRank - b.finalRank).slice(0, 6);
  return (
    <Stack spacing={0.5}>
      {top.map((t) => {
        const medal = MEDAL_COLORS[t.finalRank];
        return (
          <Box
            key={t.teamId}
            sx={{
              display: 'grid',
              gridTemplateColumns: '48px 60px 1fr auto',
              alignItems: 'center',
              gap: 2,
              px: 1,
              py: 0.5,
              bgcolor: medal ? 'rgba(255,255,255,0.18)' : 'transparent',
              borderRadius: 1,
            }}
          >
            <Typography sx={{ fontWeight: 900, fontSize: '1.75rem', lineHeight: 1 }}>{t.finalRank}</Typography>
            <Typography sx={{ fontFamily: 'monospace', opacity: 0.8 }}>#{t.startNumber}</Typography>
            <Typography sx={{ fontWeight: 700, fontSize: '1.25rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {t.teamName}
            </Typography>
            <Typography sx={{ fontFamily: 'monospace', fontWeight: 800, fontSize: '1.5rem' }}>
              {t.totalPoints} b.
            </Typography>
          </Box>
        );
      })}
    </Stack>
  );
};
